import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, comics, type Comic, type CategoryMeta } from '@/mocks/comics';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

type SortKey = 'hot' | 'new' | 'rating';

function toCategoryKey(value: string): 'fantasy' | 'campus' | 'romance' {
  return value === 'fantasy' || value === 'campus' || value === 'romance' ? value : 'fantasy';
}

export default function CategoryView({ type }: { type: string }) {
  const key = toCategoryKey(type);
  const meta: CategoryMeta = categories.find((c) => c.key === key) as CategoryMeta;

  const allTags = useMemo(() => {
    const set = new Set<string>();
    comics.filter((c) => c.category === key).forEach((c) => c.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [key]);

  const [activeTag, setActiveTag] = useState<string>('全部');
  const [statusFilter, setStatusFilter] = useState<string>('全部');
  const [sort, setSort] = useState<SortKey>('hot');

  const filtered = useMemo(() => {
    let list = comics.filter((c) => c.category === key);
    if (activeTag !== '全部') list = list.filter((c) => c.tags.includes(activeTag));
    if (statusFilter !== '全部') list = list.filter((c) => c.status === statusFilter);
    if (sort === 'new') list = [...list].sort((a, b) => b.chapters - a.chapters);
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [key, activeTag, statusFilter, sort]);

  const otherCategories = categories.filter((c) => c.key !== key);

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative overflow-hidden pt-24">
        <img
          src={meta.cover}
          alt={`${meta.name}背景图`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-background-50" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 text-background-100">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.name }]} />
          </div>
          <h1 className="font-heading text-3xl text-background-50 md:text-5xl">{meta.name}</h1>
          <p className="mt-3 text-base text-background-200 md:text-lg">{meta.tagline}</p>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">{meta.desc}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-600">题材标签：</span>
            {['全部', ...allTags].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  activeTag === tag
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="flex flex-col justify-between gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-600">连载状态：</span>
              {['全部', '连载中', '已完结'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    statusFilter === s
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-700'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-600">排序：</span>
              {(
                [
                  ['hot', '最热'],
                  ['new', '最新'],
                  ['rating', '评分'],
                ] as [SortKey, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSort(value)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    sort === value
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-background-100 py-20 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-600">该条件下暂无作品，换个筛选试试</p>
          </div>
        )}
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <h2 className="font-heading text-2xl text-foreground-950">还想看别的题材？</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {otherCategories.map((cat) => (
              <Link
                key={cat.key}
                to={`/${cat.key}`}
                className="group flex items-center gap-5 overflow-hidden rounded-2xl bg-background-50 p-4 ring-1 ring-background-200/70 transition-all hover:-translate-y-1 hover:ring-primary-300"
              >
                <img
                  src={cat.cover}
                  alt={`${cat.name}分类入口`}
                  className="h-20 w-28 rounded-xl object-cover object-top"
                />
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground-950">{cat.name}</h3>
                  <p className="mt-1 text-xs text-foreground-600">{cat.tagline}</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export type { Comic };